/**
 * Resolves a company source id from an id, code, or display name
 * using company sources returned by the company sources API.
 *
 * @param {number|string|null|undefined} companySource
 * @param {Array<{id: number|string, code?: string, displayName?: string}>} [companySources]
 * @returns {number|null}
 */
export function resolveSubstitutionCompanySourceId(companySource, companySources = []) {
    if (companySource == null || String(companySource).trim() === '') {
        return null;
    }

    const sources = Array.isArray(companySources) ? companySources : [];
    const text = String(companySource).trim();
    const normalized = text.toLowerCase();

    const numericId = Number(text);
    if (Number.isFinite(numericId) && String(numericId) === text) {
        const byId = sources.find((source) => Number(source?.id) === numericId);
        return byId ? Number(byId.id) : null;
    }

    const byCode = sources.find((source) => String(source?.code ?? '').toLowerCase() === normalized);
    if (byCode) return Number(byCode.id);

    const byDisplayName = sources.find((source) => String(source?.displayName ?? '').toLowerCase() === normalized);
    if (byDisplayName) return Number(byDisplayName.id);

    return null;
}

/**
 * Keeps item master rows mapped to a company source.
 * An empty company source leaves the list unchanged. A company source missing from the API list matches nothing.
 *
 * @param {Array<Object>|null|undefined} itemMasters
 * @param {number|string|null|undefined} [companySource]
 * @param {Array<{id: number|string, code?: string, displayName?: string}>} [companySources]
 * @returns {Array<Object>}
 */
export function filterItemMastersByCompanySource(itemMasters, companySource, companySources = []) {
    const rows = Array.isArray(itemMasters) ? itemMasters : [];
    if (companySource == null || String(companySource).trim() === '') {
        return rows;
    }

    const companySourceId = resolveSubstitutionCompanySourceId(companySource, companySources);
    if (companySourceId == null) {
        return [];
    }

    return rows.filter((row) =>
        (row?.companySources ?? []).some((source) => Number(source?.companySourceId) === companySourceId),
    );
}
